import { ClassroomType } from "@/types/classrooms";
import useStore, { ClasswizeState } from "@/stores/index";
import { formatMsgDisplayTime, getSelectedClassId } from "@/stores/chatStore";
import { getClassroomStore } from "@/stores/classroomStore";
import { getFeatureFlags } from "@/stores/featureTogglesStore";
import { WindowData } from "@/js/components/RegularClassroomUser/utils";
import { ChatMessage } from "../components/ClassroomChat/types";

/**
 * Returns true if the windowData's platform_type supports chat (chrome or companion)
 */
export function isPlatformChatSupported(windowData: WindowData): boolean {
	if (!windowData?.platform_type || typeof windowData.platform_type !== "string") {
		return false;
	}

	const platformType = windowData.platform_type.trim().toLowerCase();
	return platformType === "chrome" || platformType === "companion";
}

/**
 * Returns true if chat is disabled for the given classroom lookup id.
 * Safe when the classroom is missing from the store (returns true).
 */
export function selectChatDisabled(state: ClasswizeState, classroomLookupId: string): boolean {
	if (!classroomLookupId) {
		return true;
	}

	const classroom = getClassroomStore(state).getClassroomV2(classroomLookupId);
	if (!classroom?.type) {
		return true;
	}

	if (classroom.type.toLowerCase() === ClassroomType.Group) {
		return true;
	}

	return classroom.zoom?.policy?.chat_blocked === true;
}

/**
 * Returns true if chat is disabled for the given classroomId.
 * If classroomId is not provided, uses the selected classroom.
 */
export function getChatDisabled(classroomId?: string): boolean {
	const state = useStore.getState();
	const lookupId = classroomId || getSelectedClassId(state) || "";
	return selectChatDisabled(state, lookupId);
}

/**
 * Returns true if the chat feature is enabled for the current user.
 */
export function isChatFeatureEnabled(): boolean {
	const featureFlags = getFeatureFlags(useStore.getState());
	return !!(featureFlags && featureFlags["classwize-teacher-student-chat"]);
}

/**
 * Format message to show timestamp / avatar
 */
export function formatMsgTs(index: number, msgs: ChatMessage[]): ChatMessage[] {
	const today = new Date();

	if (index === 0) {
		msgs[index].showAvatar = true;
	}

	if (index === msgs.length - 1) {
		msgs[index].renderTs = formatMsgDisplayTime(today, msgs[index].timestamp);
		return msgs;
	}

	if (msgs[index].sender.username !== msgs[index + 1].sender.username) {
		msgs[index].renderTs = formatMsgDisplayTime(today, msgs[index].timestamp);
		msgs[index + 1].showAvatar = true;
		return msgs;
	}

	const startDate = new Date(msgs[index].timestamp);
	const nextDate = new Date(msgs[index + 1].timestamp);
	const diffInMinutes = Math.floor((startDate.getTime() - nextDate.getTime()) / 60000);
	// if current date and the next one is within 15 minutes of each other
	const showTs = diffInMinutes > 15;
	msgs[index].renderTs = showTs ? formatMsgDisplayTime(today, msgs[index].timestamp) : null;
	msgs[index + 1].showAvatar = showTs;

	return msgs;
}
